import React, { useEffect, useState, useCallback } from 'react';
import { MdReportProblem, MdCheck, MdClose, MdRefresh } from 'react-icons/md';
import { duplicateStatusToUnified, ACTION_HINT } from '../lib/statusVocab';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

type DupItem = {
  id: number;
  ref_number: string;
  pdf_rowid: string | number;
  partner_id: number;
  partner_name: string;
  issue_date: string;
  amount: number;
  status: 'duplicate_rejected' | 'duplicate_review';
  dedup_action: string | null;
  dedup_reason: string | null;
  dedup_candidates: Array<{
    method: string;
    direction: string;
    related: string[];
    sum_related: number;
    diff_amount: number;
    diff_ratio: number;
  }>;
  proposed_at: string;
  notes: string;
};

export const DuplicateCandidatesPage: React.FC = () => {
  const [items, setItems] = useState<DupItem[]>([]);
  const [counts, setCounts] = useState({ auto_rejected: 0, review_pending: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/duplicate-candidates`);
      const j = await res.json();
      setItems(j.items || []);
      setCounts(j.counts || { auto_rejected: 0, review_pending: 0 });
    } catch (e) {
      console.error('[DuplicateCandidatesPage]', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id: number, action: 'confirm_duplicate' | 'mark_unique') => {
    setBusy(id);
    try {
      await fetch(`${API_BASE}/api/duplicate-candidates/${id}/resolve`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      await load();
    } catch (e) {
      console.error('[resolve]', e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <MdReportProblem size={24} color="#d97706" />
        <h2 style={{ margin: 0, fontSize: 20 }}>重複疑い 請求書</h2>
        <button
          onClick={load}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '6px 12px', borderRadius: 4, border: '1px solid #cbd5e1',
            background: '#fff', cursor: 'pointer', marginLeft: 'auto',
          }}
        >
          <MdRefresh size={16} /> 再読込
        </button>
      </div>
      {/* 1行の行動説明 (統一語彙: 対応待ち(判定) = 修正ではなく裁定) */}
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
        対応待ち(重複判定) — {ACTION_HINT.judge}: 「重複確定」か「別請求として採用」を選ぶと前進します
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <div style={{ padding: 12, background: duplicateStatusToUnified('duplicate_rejected').bg, border: `1px solid ${duplicateStatusToUnified('duplicate_rejected').bd}`, borderRadius: 8, flex: 1 }}>
          <div style={{ fontSize: 12, color: duplicateStatusToUnified('duplicate_rejected').fg }}>除外(自動判定) (= 集約版 検出・操作不要)</div>
          <div style={{ fontSize: 24, fontWeight: 600 }}>{counts.auto_rejected}</div>
        </div>
        <div style={{ padding: 12, background: duplicateStatusToUnified('duplicate_review').bg, border: `1px solid ${duplicateStatusToUnified('duplicate_review').bd}`, borderRadius: 8, flex: 1 }}>
          <div style={{ fontSize: 12, color: duplicateStatusToUnified('duplicate_review').fg }}>対応待ち(重複判定) (= 単件重複疑い・あなたの裁定が必要)</div>
          <div style={{ fontSize: 24, fontWeight: 600 }}>{counts.review_pending}</div>
        </div>
      </div>

      {loading && <div>読込中...</div>}
      {!loading && items.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
          重複疑い案件 なし
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
            <th style={{ padding: 8 }}>ID</th>
            <th style={{ padding: 8 }}>rid</th>
            <th style={{ padding: 8 }}>取引先</th>
            <th style={{ padding: 8 }}>請求日</th>
            <th style={{ padding: 8, textAlign: 'right' }}>金額</th>
            <th style={{ padding: 8 }}>状態</th>
            <th style={{ padding: 8 }}>判定理由 + 関連 ref</th>
            <th style={{ padding: 8 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: 8 }}>{r.id}</td>
              <td style={{ padding: 8 }}>{r.pdf_rowid}</td>
              <td style={{ padding: 8 }}>{r.partner_name}</td>
              <td style={{ padding: 8 }}>{r.issue_date}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{r.amount?.toLocaleString()}</td>
              <td style={{ padding: 8 }}>
                {(() => {
                  const u = duplicateStatusToUnified(r.status);
                  return (
                    <span title={r.status} style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11,
                      background: u.bg, color: u.fg, border: `1px solid ${u.bd}`,
                    }}>
                      {u.label}
                    </span>
                  );
                })()}
              </td>
              <td style={{ padding: 8, fontSize: 11, color: '#475569' }}>
                {r.dedup_reason && <div>{r.dedup_reason}</div>}
                {r.dedup_candidates?.map((c, i) => (
                  <div key={i}>
                    <code style={{ fontSize: 10 }}>{c.method} / {c.direction}</code>
                    <br />
                    related: {c.related.join(', ')} (sum=¥{c.sum_related?.toLocaleString()})
                  </div>
                ))}
              </td>
              <td style={{ padding: 8 }}>
                {r.status === 'duplicate_review' && (
                  <>
                    <button
                      onClick={() => resolve(r.id, 'confirm_duplicate')}
                      disabled={busy === r.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', marginRight: 4, fontSize: 11,
                        background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                      }}
                    >
                      <MdCheck size={14} /> 重複確定
                    </button>
                    <button
                      onClick={() => resolve(r.id, 'mark_unique')}
                      disabled={busy === r.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 8px', fontSize: 11,
                        background: '#0284c7', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                      }}
                    >
                      <MdClose size={14} /> 別請求として採用
                    </button>
                  </>
                )}
                {r.status === 'duplicate_rejected' && (
                  <button
                    onClick={() => resolve(r.id, 'mark_unique')}
                    disabled={busy === r.id}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 8px', fontSize: 11,
                      background: '#0284c7', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    <MdClose size={14} /> reject 取消 (= 個別採用)
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
